import { useDispatch, useSelector } from "react-redux";
import { FiUsers, FiActivity } from "react-icons/fi";
import { FaUserCheck } from "react-icons/fa";
import "../../office-home-tabs/OfficeTabs.css";
import "../../../../styles/screens/client-visit-details/manage-module/EventsList.css";
import "../../../../styles/screens/client-visit-screens/CreateNewVisitForm.css";
import "../../../../styles/screens/client-visit-details/manage-module/AddNewEventForm.css";
import "../../../../styles/screens/Analytics/Analytics.css";
import { useState, useEffect } from "react";
import Select from "react-select";
import { AnalyticsStringLabel } from "../../../../util/Strings";
import Required from "../../../common-components/Required";
import React from "react";
import {
  getUserbasedAnalytics,
  clearAnalytics,
  getVisitbasedAnalytics,
} from "../../../../slices/AnalyticsSlice";
import { getVisitsLast12Months } from "../../../../slices/VisitSlice";
import { getAllUsers } from "../../../../slices/UserSlice";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { GetDate } from "../../../../util/FilterDate";
import { ClientVisitScreenStringLabels } from "../../../../util/Strings";
import BlackButton from "../../../custom-components/BlackButton";
import ErrorMessageComponent from "../../../common-components/ErrorMessageComponent";

ChartJS.register(ArcElement, Tooltip, Legend);

// Custom styles for react-select to fix dropdown clipping
const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const Analytics = ({ fromOffice }) => {
  const dispatch = useDispatch();

  const visitEndDate = useSelector((state) => state.visits.selectedVisitEndDate);
  const visitId = useSelector((state) => state.visits.selectedVisitId);
  const selectedOfficeId = useSelector((state) => state.officelist.selectedOfficeId);

  // Redux state for users and visits
  const userOptions = useSelector((state) => state.users.userOptions) || [];
  const analyticsData = useSelector((state) => state.analytics.analyticsData) || {};
  const visitBasedAnalytics = analyticsData.visitBasedAnalytics;

  // Destructure metrics safely
  const {
    averageDuration,
    averageSessions,
    averageScreenView,
    averageSessionsDuration,
    topScreensData,
    topClicksData,
  } = analyticsData;

  // Determine if a user-based fetch has happened (so we know to show the message)
  const analyticsLoaded =
    (analyticsData && analyticsData.usersData && analyticsData.usersData.length > 0) ||
    (analyticsData && analyticsData.topScreensData?.length > 0);

  // Determine if the analytics data is valid — averageDuration must be greater than 0
  const hasValidAnalytics = averageDuration > 0;

  // Component state + validation
  const [state, setState] = useState({});
  const [validation, setValidation] = useState({});

  // Load users on mount if not fromOffice
  useEffect(() => {
    if (!fromOffice) {
      dispatch(getAllUsers(visitId));
    }
    return () => {
      dispatch(clearAnalytics());
      setState({});
      setValidation({});
    };
  }, [dispatch, fromOffice, visitId]);

  // Load visit based analytics if fromOffice and selectedOfficeId set
  useEffect(() => {
    if (fromOffice && selectedOfficeId) {
      dispatch(getVisitbasedAnalytics({ officeId: selectedOfficeId }));
      dispatch(getVisitsLast12Months(selectedOfficeId));
    }
  }, [dispatch, fromOffice, selectedOfficeId]);

  // When analytics data loads but averageDuration is 0, show message in the existing validation slot
  useEffect(() => {
    if (analyticsLoaded && averageDuration === 0) {
      setValidation((prev) => ({ ...prev, userId: AnalyticsStringLabel.noValidDataMessage} ));
    }
  }, [analyticsLoaded, averageDuration]);

  const visitsLast12Months = useSelector((state) => state.visits.visitDataLast12Months) || [];
  const visitOptions = visitsLast12Months.map((visit) => ({ value: visit._id, label: visit.name }));

  // Handle input change for date inputs
  function handleChange(e) {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
  }

  // Trigger analytics fetch ONLY on submit
  const handleNextButton = () => {
    if (state.userIds && state.userIds.length > 0) {
      dispatch(
        getUserbasedAnalytics({
          userIds: state.userIds,
          startDate: state.startDate,
          endDate: state.endDate,
          visitId: state.visitId,
        })
      );
      setValidation((prev) => ({ ...prev, userId: "" }));
    } else {
      setValidation((prev) => ({ ...prev, userId: AnalyticsStringLabel.noValidDataMessage }));
    }
  };

  // Clear button handler - resets everything and shows Visit Summary again
  const handleClear = () => {
    // Clear analytics data (user-based) but re-fetch visit-based analytics
    dispatch(clearAnalytics());
    setState({});
    setValidation({});

    // Re-fetch visit-based analytics to show Visit Summary card again
    if (fromOffice && selectedOfficeId) {
      dispatch(getVisitbasedAnalytics({ officeId: selectedOfficeId }));
    }
  };

  // Handle selection changes - ONLY updates state, no API call, no validation messages
  const handleUserOptions = (selectedOptions) => {
    // Clear validation on every new selection
    setValidation((prev) => ({ ...prev, userId: "" }));
    // Clear previous analytics data on new selection
    dispatch(clearAnalytics());

    if (!fromOffice) {
      // Multi-select of users
      const userIds = selectedOptions ? selectedOptions.map((user) => user.value) : [];
      setState((prev) => ({ ...prev, users: selectedOptions, userIds, visitId: null }));
    } else {
      // Single-visit select - only update state
      if (selectedOptions && selectedOptions.value) {
        const visit = visitsLast12Months.find((v) => v._id === selectedOptions.value);
        const users = visit?.users || [];
        setState((prev) => ({ ...prev, userIds: users, visitId: selectedOptions.value }));
      } else {
        // Selection cleared
        setState((prev) => ({ ...prev, visitId: null, userIds: [] }));
      }
    }
  };

  // Return top viewed screen name and view count for user's screensArray
  const screenNames = (item) => {
    const arr = item.screensArray || [];
    if (arr.length === 0) return "";
    const screenData = arr.reduce((prev, curr) => (prev.viewCount > curr.viewCount ? prev : curr));
    return `${screenData.screenName} : ${screenData.viewCount}`;
  };

  // Chart options for pie charts
  const options = {
    plugins: {
      legend: {
        position: "left",
      },
    },
  };

  // Convert seconds to readable format
  const timeConv = (seconds) =>
    seconds > 60
      ? `${Math.floor(seconds / 60)} ${Math.floor(seconds / 60) > 1 ? "Mins" : "Min"}`
      : `${seconds} ${seconds > 1 ? "Seconds" : "Second"}`;

  return (
    <div className="users-list-container analytics-container">
      <div className="users-list-header-container">
        <div className="event-list-heading">Analytics</div>
      </div>

      {/* Show Visit Summary card only if fromOffice AND there is visitBasedAnalytics data AND topScreensData DON'T exist */}
      {fromOffice && visitBasedAnalytics && !(analyticsData.topScreensData?.length > 0) && (
        <VisitSummaryCard visitAnalytics={visitBasedAnalytics} />
      )}

      <div className="analytics-form-row">
        <div className="form-label-input">
          <label className="form-label">
            {fromOffice ? "Select visit" : "Select Users"} <Required />
          </label>
          <Select
            options={fromOffice ? visitOptions : userOptions}
            className="basic-multi-select"
            classNamePrefix="select"
            name="users"
            isMulti={!fromOffice}
            onChange={handleUserOptions}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectStyles}
            value={
              !fromOffice
                ? state.users || null
                : state.visitId
                ? visitOptions.find((v) => v.value === state.visitId) || null
                : null
            }
          />
          <ErrorMessageComponent message={validation.userId} />
        </div>
        <div className="form-label-input analytics-form-date-input">
          <label className="form-label">{ClientVisitScreenStringLabels.startDate}</label>
          <div className="form-input-container">
            <input
              className="form-input"
              type="date"
              name="startDate"
              value={state.startDate || ""}
              onChange={handleChange}
              placeholder="Input"
              required
            />
          </div>
        </div>
        <div className="form-label-input analytics-form-date-input">
          <label className="form-label">{ClientVisitScreenStringLabels.endDate}</label>
          <div className="form-input-container">
            <input
              className="form-input"
              type="date"
              name="endDate"
              value={state.endDate || ""}
              onChange={handleChange}
              placeholder="Input"
              required
              max={GetDate(visitEndDate)}
            />
          </div>
        </div>
        <div className="analytics-submit-btn-container">
          <BlackButton onClickhandle={handleNextButton}>Submit</BlackButton>
        </div>
         <div className="analytics-submit-btn-container analytics-clear-btn">
          <BlackButton onClickhandle={handleClear}>Clear</BlackButton>
        </div>
      </div>

      {/* User Based Analytics UI when averageDuration > 0 */}
      {hasValidAnalytics && (
        <>
          {!fromOffice && (
            <div className="analytics-heading">Selected Users Analytics</div>
          )}
          <div className="analytics-metrics-row">
            <MetricCard
              title="Top 5 Screens"
              value={topScreensData.map((item, index) => (
                <div key={index}>{`${item.screenName} Screen - ${item.viewCount}`}</div>
              ))}
            />
            <div className="metric-card analytics-pie-chart-container">
              <Pie data={getChartData(topScreensData)} options={options} />
            </div>
            {topClicksData && topClicksData.length > 0 && (
              <MetricCard
                title="Top 3 Clicks"
                value={topClicksData.map((item, index) => (
                  <div key={index}>{`${item.screenName} Click - ${item.viewCount}`}</div>
                ))}
              />
            )}
            <MetricCard
              title="Analytics Overview"
              value={
                <div className="analytics-overview-content">
                  <div>Avg Screen Counts: {averageScreenView}</div>
                  <div>Avg Time Spent: {timeConv(averageDuration)}</div>
                  <div>Avg Sessions: {averageSessions}</div>
                  <div>Avg Session Time: {timeConv(averageSessionsDuration)}</div>
                  {fromOffice && (
                    <>
                      <div>
                        Client Users: {analyticsData.totalClientUsersInVisits} (
                        {analyticsData.totalClientUsersUsedAppInVisits} used the App)
                      </div>
                      <div>
                        Internal Users: {analyticsData.totalInternalUsersInVisits} (
                        {analyticsData.totalInternalUsersUsedAppInVisits} used the App)
                      </div>
                    </>
                  )}
                </div>
              }
            />
          </div>
        </>
      )}

      {/* User detailed cards when NOT fromOffice AND data is valid */}
      {!fromOffice &&
        hasValidAnalytics &&
        analyticsData?.usersData &&
        analyticsData.usersData.length > 0 &&
        analyticsData.usersData.map((item) => (
          <React.Fragment key={item.userId || item.name}>
            {item.totalScreensVisited > 0 || item.totalDuration > 0 ? (
              <div className="metrics">
                <MetricCard
                  title={`Username - ${item.name || item.userId}`}
                  value={
                    <>
                      <div>{screenNames(item)} Times</div>
                      <div>Total Duration: {timeConv(item.totalDuration)}</div>
                      <div>Total Screens Visited: {item.totalScreensVisited}</div>
                      <div>Total Sessions: {item.totalSessions}</div>
                    </>
                  }
                />
                {item.screensArray && item.screensArray.length > 0 && (
                  <div className="metric-card analytics-pie-chart-container">
                    <Pie data={getChartData(item.screensArray)} options={options} />
                  </div>
                )}
                <MetricCard
                  title={`Total Screen Views: ${item.totalScreensVisited}`}
                  value={item.screensArray.map((screen, idx) => (
                    <div key={idx}>{`${screen.screenName} - ${screen.viewCount}`}</div>
                  ))}
                />
              </div>
            ) : (
              <div className="metrics">
                <MetricCard title={`Username: ${item.name || item.userId} - No data available`} />
              </div>
            )}
          </React.Fragment>
        ))}
    </div>
  );
};

const VisitSummaryCard = ({ visitAnalytics }) => {
  const visitSummaryMetrics = [
    { label: "Total Visits", value: visitAnalytics.totalVisits, icon: <FiActivity /> },
    { label: "Total Users", value: visitAnalytics.totalUsersInVisits, icon: <FiUsers /> },
    { label: "Total Client Users", value: visitAnalytics.totalClientUsersInVisits, icon: <FiUsers /> },
    { label: "Total Internal Users", value: visitAnalytics.totalInternalUsersInVisits, icon: <FiUsers /> },
  ];

  const appUsageMetrics = [
    {
      label: "Active Users",
      value: visitAnalytics.totalUsersUsedAppInVisits,
      icon: (
        <span className="visit-summary-app-icon">
          <FaUserCheck className="visit-summary-app-icon-size" />
        </span>
      ),
    },
    {
      label: "Active Client Users",
      value: visitAnalytics.totalClientUsersUsedAppInVisits,
      icon: (
        <span className="visit-summary-app-icon">
          <FaUserCheck className="visit-summary-app-icon-size" />
        </span>
      ),
    },
    {
      label: "Active Internal Users",
      value: visitAnalytics.totalInternalUsersUsedAppInVisits,
      icon: (
        <span className="visit-summary-app-icon">
          <FaUserCheck className="visit-summary-app-icon-size" />
        </span>
      ),
    },
  ];

  return (
    <div className="visit-summary-card">
      <h2 className="visit-summary-card-title">{AnalyticsStringLabel.visitSummaryHeading}</h2>

      <div className="visit-summary-card-content">
        <div className="visit-summary-column">
          {visitSummaryMetrics.map(({ label, value, icon }) => (
            <div key={label} className="visit-summary-metric-item">
              <span className="visit-summary-metric-icon">{icon}</span>
              {label}: <span className="visit-summary-metric-value">{value}</span>
            </div>
          ))}
        </div>

        <div className="visit-summary-column">
          {appUsageMetrics.map(({ label, value, icon }) => (
            <div key={label} className="visit-summary-metric-item">
              <span className="visit-summary-metric-icon-wrapper">{icon}</span>
              {label}: <span className="visit-summary-metric-value">{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const getChartData = (data) => {
  const screenNames = data.map((item) => item.screenName);
  const viewCounts = data.map((item) => item.viewCount);

  return {
    labels: screenNames || [],
    datasets: [
      {
        label: "Screen Views",
        data: viewCounts || [],
        backgroundColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
        ],
      },
    ],
  };
};

const MetricCard = ({ title, value, icon }) => (
  <div className="metric-card">
    <div className="metric-card-icon">{icon && <img src={icon} alt={`${title} icon`} />}</div>
    <div className="metric-card-content">
      <h3 className="metric-card-title">{title}</h3>
      <p className="metric-card-value">{value}</p>
    </div>
  </div>
);

export default Analytics;